/**
 * The channel and modality vocabulary, in the one place both sides can read it.
 *
 * These types were defined on the platform, where the engine and the adapters
 * need them. The console needs them too — every queue row, case header and
 * transcript says which surface a citizen used — and could not import from a
 * server module, so it kept its own copy of the labels. Two copies, in fact:
 * `Queues.tsx` and `CrmCaseCitizen.tsx`, both of which still offered `voice` as
 * a channel months after it stopped being one, and neither of which had heard
 * of the two widget surfaces.
 *
 * That is the failure mode worth designing against. A label map keyed by a
 * hand-written string set drifts silently: the union grows, the map does not,
 * and the new channel renders as `site_widget` to staff until somebody notices.
 * Keying the maps here as `Record<Channel, …>` makes the compiler refuse a new
 * channel that nobody has named, which turns a silent drift into a build error.
 *
 * Only the vocabulary lives here. What a surface can *do* — renderable widgets,
 * attachment policy, identity durability — stays on the platform in
 * `channels/model.ts`, because it is a server concern the console has no use
 * for and no business deciding.
 */

/**
 * Where the citizen is.
 *
 * Telephony is deliberately absent. A phone call over SIP is a real channel
 * later — different identity, real-time, no visual surface at all — and it is a
 * different thing from a voice message, which is a `Modality` on a surface that
 * already exists.
 */
export type Channel = 'webchat' | 'whatsapp' | 'app_widget' | 'site_widget';

export const CHANNELS: readonly Channel[] = ['webchat', 'whatsapp', 'app_widget', 'site_widget'];

/**
 * How this turn is happening.
 *
 * A property of a turn, not of a conversation: a citizen sends a voice note,
 * then taps a button, then types.
 */
export type Modality = 'text' | 'voice';

export const MODALITIES: readonly Modality[] = ['text', 'voice'];

export interface ChannelCopy {
  en: string;
  ar: string;
}

/**
 * Exhaustive by construction — adding a `Channel` without a label is a type
 * error, which is the whole point of this file.
 */
export const CHANNEL_LABELS: Record<Channel, ChannelCopy> = {
  webchat: { en: 'Web chat', ar: 'المحادثة الإلكترونية' },
  whatsapp: { en: 'WhatsApp', ar: 'واتساب' },
  app_widget: { en: 'Mobile app', ar: 'تطبيق الهاتف' },
  site_widget: { en: 'Website widget', ar: 'أداة الموقع' },
};

export const MODALITY_LABELS: Record<Modality, ChannelCopy> = {
  text: { en: 'Typed', ar: 'مكتوب' },
  voice: { en: 'Voice', ar: 'صوتي' },
};

export function isChannel(value: string): value is Channel {
  return (CHANNELS as readonly string[]).includes(value);
}

export function isModality(value: string): value is Modality {
  return (MODALITIES as readonly string[]).includes(value);
}

/**
 * A label for whatever the database actually held.
 *
 * Staff-facing surfaces read rows written by older code, and a console that
 * throws — or renders nothing — because it met a value it did not expect is
 * worse than one that shows the raw slug. The unknown value is passed through
 * in both languages so the screen still says something true.
 */
export function channelLabel(value: string): ChannelCopy {
  return isChannel(value) ? CHANNEL_LABELS[value] : { en: value, ar: value };
}

export function modalityLabel(value: string): ChannelCopy {
  return isModality(value) ? MODALITY_LABELS[value] : { en: value, ar: value };
}
